/* eslint-disable indent */
import type { SelectOption } from "@cinnamon/design-system/types";
import isEmpty from "lodash-es/isEmpty";

import { EXEC_TYPE } from "@/constants/global";
import {
  DEFAULT_PREPROCESSING_WF_CONFIG,
  PREPROCESSING_WF_PAGE_PROCESS_TYPE,
  PREPROCESSING_WF_UPLOAD_MODE,
} from "@/constants/workflow";
import { getCurrentDateTime } from "@/helper/utility";
import { type DetectableClassServer, type Target } from "@/types/pipeline";
import {
  type FilenameConfigServer,
  type FilenameConfigType,
  FilenameConfigurationType,
  type PreprocessingWorkflow,
  type PreprocessingWorkflowServer,
  type Workflow,
  type WorkflowPayload,
  type WorkflowPayloadServer,
  type WorkflowServer,
} from "@/types/workflow";

import { normalizeDetectableClass, transformDetectableClassesToPayload } from "./pipeline";

export const convertContentFileNameToPayload = (content: FilenameConfigType[]): FilenameConfigServer[] => {
  return content.map((item) => {
    if (typeof item === "string") {
      return {
        type: FilenameConfigurationType.LITERAL,
        value: item,
      };
    }
    if (item.value === FilenameConfigurationType.UPLOADED_DATE) {
      return {
        type: FilenameConfigurationType.UPLOADED_DATE,
        value: null,
      };
    }
    return {
      type: FilenameConfigurationType.PLACEHOLDER,
      value: String(item.value),
    };
  });
};

export const convertWfToPayloadServer = (wf: WorkflowPayload): WorkflowPayloadServer => {
  const { pre_processors: preprocessingParam, detectable_classes: detectableClassesParams } = wf.executor;

  const preprocessors = convertPreprocessingWfToServer(preprocessingParam);
  let detectableClasses: DetectableClassServer[] | undefined = undefined;
  if (wf.exec_type === EXEC_TYPE.CLASSIFICATION) {
    detectableClasses = transformDetectableClassesToPayload(detectableClassesParams || []);
  }

  const filenameConfig = convertContentFileNameToPayload(wf.executor.filename_config ?? []);

  return {
    ...wf,
    executor: {
      ...wf.executor,
      pre_processors: preprocessors,
      detectable_classes: detectableClasses,
      filename_config: filenameConfig,
    },
  };
};

export const convertPreprocessingWfToServer = (
  preprocessingParam: PreprocessingWorkflow
): PreprocessingWorkflowServer | undefined => {
  const { processingPages, rotation, uploadMode, pagesPerFile } = preprocessingParam;

  const isSpecificPage = processingPages.type === PREPROCESSING_WF_PAGE_PROCESS_TYPE.SPECIFIC_PAGE;

  const splitPagesPerJob = pagesPerFile ? Number(pagesPerFile) : undefined;

  /*
   * Need to convert processing_pages to string
   * ex: ["1", "3"] => "1-3" | ["1"] => "1" | ["1", ""] => "1-" | ["", "1"] => "-1" | null => "all"
   */
  const preprocessors: PreprocessingWorkflowServer | undefined = processingPages
    ? {
        upload_mode: uploadMode,
        rotation,
        processing_pages: isSpecificPage
          ? (Number(processingPages.value[0]) - 1).toString()
          : processingPages.type === PREPROCESSING_WF_PAGE_PROCESS_TYPE.SPECIFIC_RANGE
            ? processingPages.value.map((item) => (item === "" ? "" : (Number(item) - 1).toString())).join("-")
            : PREPROCESSING_WF_PAGE_PROCESS_TYPE.ALL,
        split_pages_per_job: splitPagesPerJob,
      }
    : undefined;

  return preprocessors;
};

export const convertFilenameConfigFromServer = (
  filenameConfig: FilenameConfigServer[] | undefined,
  targetFields: Record<string, Target | null> | undefined
): Array<string | SelectOption> => {
  if (!filenameConfig) return [];

  const keyObj = Object.keys(targetFields ?? {});
  return filenameConfig.map((item) => {
    if (item.type === FilenameConfigurationType.UPLOADED_DATE) {
      return {
        label: getCurrentDateTime(),
        value: FilenameConfigurationType.UPLOADED_DATE,
      };
    }

    if (item.type === FilenameConfigurationType.PLACEHOLDER) {
      if (keyObj.includes(item.value)) {
        return {
          label: targetFields?.[item.value]?.display_name ?? "",
          value: item.value,
        };
      }
      return {
        label: item.value,
        value: item.value,
      };
    }

    if (item.type === FilenameConfigurationType.LITERAL) {
      return item.value;
    }

    return item;
  });
};

export const normalizeWf = (wf: WorkflowServer): Workflow => {
  if (wf.executor.pre_processors && !isEmpty(wf.executor.pre_processors)) {
    const { pre_processors: preprocessingParam } = wf.executor;
    const { processing_pages: processingPages } = preprocessingParam;

    /*
     * ProcessingPages is string, so we need to convert it to number
     * ex: "1-3" => ["1", "3"] | "1" => ["1"] | "1-" => ["1", ""] | "-1" => ["", "1"] | "all" => null
     */
    const preProcessors: PreprocessingWorkflow = {
      uploadMode: preprocessingParam?.upload_mode ?? PREPROCESSING_WF_UPLOAD_MODE.WHOLE_FILE,
      rotation: preprocessingParam.rotation,
      processingPages:
        processingPages !== PREPROCESSING_WF_PAGE_PROCESS_TYPE.ALL
          ? processingPages.includes("-")
            ? {
                type: PREPROCESSING_WF_PAGE_PROCESS_TYPE.SPECIFIC_RANGE,
                value: processingPages.split("-").map((item) => (item === "" ? "" : (Number(item) + 1).toString())),
              }
            : {
                type: PREPROCESSING_WF_PAGE_PROCESS_TYPE.SPECIFIC_PAGE,
                value: [(Number(processingPages) + 1).toString()],
              }
          : {
              type: PREPROCESSING_WF_PAGE_PROCESS_TYPE.ALL,
              value: null,
            },
      pagesPerFile: preprocessingParam.split_pages_per_job?.toString() ?? DEFAULT_PREPROCESSING_WF_CONFIG.pagesPerFile,
    };

    return {
      ...wf,
      executor: {
        ...wf.executor,
        detectable_classes: normalizeDetectableClass(wf.executor?.detectable_classes ?? []),
        pre_processors: preProcessors,
        enabled_filename_config: wf.executor.enabled_filename_config ?? false,
        filename_config: convertFilenameConfigFromServer(wf.executor.filename_config, wf.executor.targets),
      },
    };
  }

  return wf as unknown as Workflow;
};
